import { Box, Divider, Grid, Typography } from '@mui/material';
import HeaderBreadcrumb from '../shared-components/HeaderBreadcrumb';
import { CategorySelection, CompanySelection, DataCapturingCategorySelection, FacilitySelection, GroupSelection, YearSelection } from '../shared-components/SelectionButtons';
import DataCapturingTable from '../categories/shared-components/DataCapturingTable';
import TotalTable from '../categories/shared-components/TotalTable';
import { useCategoryOneDataStore } from '../categories/hooks/category-one-hooks';
import { useCategoryTwoDataStore } from '../categories/hooks/category-two-hooks';
import { useCategoryThreeDataStore } from '../categories/hooks/category-three-hooks';
import { useCategoryFourDataStore } from '../categories/hooks/category-four-hooks';
import { useCategoryFiveDataStore } from '../categories/hooks/category-five-hooks';
import AgrochemicalPlotList from '../categories/category-one/PlotlistTableAgrochemicals';
import FertilizerPlotListTable from '../categories/category-one/PlotListTableFertilizers';
import useButtonSelection from '../hooks/button-selection-hooks';
import { MONTHS } from '../categories/constants/months';

export default function DataCapturing({ variant, company, years, groups }) {
    const {
        selectedYear,
        selectedCompany,
        selectedGroup,
        selectedFacility,
        categories,
        selectedCategory,
        selectedDataCapturingCategory,
        handleOnClick } = useButtonSelection(variant, company, years, groups)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <HeaderBreadcrumb variant={variant} year={selectedYear} company={selectedCompany} group={selectedGroup}
                facility={selectedFacility} category={selectedCategory} dataCapturingCategory={selectedDataCapturingCategory} />
            <Grid container item spacing={0} xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                <YearSelection years={years} selectedYear={selectedYear} handleOnClick={handleOnClick} />
                {selectedYear &&
                    <CompanySelection company={company} selectedCompany={selectedCompany} handleOnClick={handleOnClick} />}
                {selectedCompany &&
                    <GroupSelection groups={groups} selectedGroup={selectedGroup} handleOnClick={handleOnClick} />}
                {selectedGroup &&
                    <FacilitySelection selectedGroup={selectedGroup} selectedFacility={selectedFacility} handleOnClick={handleOnClick} />}
                {selectedFacility &&
                    <CategorySelection categories={categories} selectedCategory={selectedCategory} handleOnClick={handleOnClick} />}
            </Grid>
            {
                selectedDataCapturingCategory &&
                <DataCapturingButtonsAndTable
                    year={selectedYear}
                    companyId={selectedCompany.id}
                    groupId={selectedGroup.id}
                    facilityId={selectedFacility.id}
                    category={selectedCategory}
                    dataCapturingCategory={selectedDataCapturingCategory}
                    handleOnClick={handleOnClick}
                />
            }
        </Box>
    )
}

function DataCapturingTableWrapper({ category, dataCapturingCategory, year, companyId, groupId, facilityId }) {

    const categoryOneStore = useCategoryOneDataStore(dataCapturingCategory, year, companyId, groupId, facilityId);
    const categoryTwoStore = useCategoryTwoDataStore(dataCapturingCategory, year, companyId, groupId, facilityId);
    const categoryThreeStore = useCategoryThreeDataStore(dataCapturingCategory, year, companyId, groupId, facilityId);
    const categoryFourStore = useCategoryFourDataStore(dataCapturingCategory, year, companyId, groupId, facilityId);
    const categoryFiveStore = useCategoryFiveDataStore(dataCapturingCategory, year, companyId, groupId, facilityId);

    let store;
    switch (category.name) {
        case "Category One":
            store = categoryOneStore;
            break;
        case "Category Two":
            store = categoryTwoStore;
            break;
        case "Category Three":
            store = categoryThreeStore;
            break;
        case "Category Four":
            store = categoryFourStore;
            break;
        case "Category Five":
            store = categoryFiveStore;
            break;
        default:
            store = {};
    }

    const {
        month,
        setMonth,
        data,
        totals,
        getDataAction,
        getTotalsAction,
        postDataAction,
        uploadCsvDocument,
        categoryKey,
        distributionType,
        columns,
        totalColumns,
        grandTotalFields,
        generalInformationData,
        fuelType,
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = store;
    return (
        <DataCapturingTable
            month={month}
            setMonth={setMonth}
            months={MONTHS}
            columns={columns}
            data={data}
            totals={totals}
            getDataAction={getDataAction}
            getTotalsAction={getTotalsAction}
            postDataAction={postDataAction}
            uploadCsvAction={uploadCsvDocument}
            TotalTableComponent={({ dataTotals }) => (
                <TotalTable
                    dataTotals={dataTotals}
                    columns={totalColumns}
                    grandTotalFields={grandTotalFields}
                />
            )}
            selectedCategory={dataCapturingCategory}
            categoryKey={categoryKey}
            distributionType={distributionType}
            fuelType={fuelType}
            generalInformationData={generalInformationData}
            year={year}
            companyId={companyId}
            groupId={groupId}
            facilityId={facilityId}
            totalRecordCount={totalRecordCount}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            _determinePaginationMessage={_determinePaginationMessage}
            _determinePaginationDisabledState={_determinePaginationDisabledState}
            _determineRowsPerPageOptions={_determineRowsPerPageOptions}
            _handleChangeRowsPerPage={_handleChangeRowsPerPage}
            _handleChangePage={_handleChangePage}
        />
    )
}

function DataCapturingButtonsAndTable({ year, companyId, groupId, facilityId, category, dataCapturingCategory, handleOnClick }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Divider />
                <Typography variant='h5' >
                    Data Capturing
                </Typography>
            </Box>
            <Grid container item spacing={3}>
                <Grid item xs={2}>
                    <DataCapturingCategorySelection
                        selectedDataCapturingCategory={dataCapturingCategory}
                        handleOnClick={handleOnClick}
                        categories={category.categories}
                    />
                </Grid>
                <Grid item xs={10}>
                    {dataCapturingCategory === 'Agrochemicals Applied'
                        ? <AgrochemicalPlotList year={year} groupId={groupId} />
                        : dataCapturingCategory === 'Fertilizer Applied'
                            ? <FertilizerPlotListTable year={year} groupId={groupId} />
                            : <DataCapturingTableWrapper category={category} dataCapturingCategory={dataCapturingCategory}
                                year={year} companyId={companyId} groupId={groupId}
                                facilityId={facilityId} />
                    }
                </Grid>
            </Grid>
        </Box>
    )
}